import { favoriteActions } from '@/actions';
import Card from '@/components/elements/Card/Card';
import Companies from '@/components/elements/Card/Companies';
import { EmployeeListCard } from '@/components/elements/Card/EmployeeListCard';
import { Button, Fab, LinkButton } from '@/components/elements/forms/buttons';
import SearchInput from '@/components/elements/forms/SearchInput/SearchInput';
import { ListItem } from '@/components/elements/lists';
import Logotype from '@/components/elements/Logotype';
import useReviews, { REVIEWS_PAGE_LIMIT } from '@/hooks/useReviews';
import type { SelectedTimeFilterOptions } from '@/types/reviews';
import type { ReviewsModalType } from '@/types/modal';

import Alert from '@/components/elements/notifications/Alert/Alert';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import Icon from '@/components/icons/Icon';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import Breadcrumbs from '@/components/modules/breadcrumbs/Breadcrumbs';
import BookAppointment from '@/components/modules/modals/BookAppointment/BookAppointment';
import { BookAppointmentContext } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import BottomNavBar from '@/components/modules/navigation/BottomNavBar/BottomNavBar';
import NavigationDrawer from '@/components/modules/navigation/NavigationDrawer/NavigationDrawer';
import Address from '@/components/modules/place/Address';
import Contact from '@/components/modules/place/Contact';
import Links from '@/components/modules/place/Links';
import OpeningHours from '@/components/modules/place/OpeningHours';
import Photos from '@/components/modules/place/Photos';
import PlaceDetails from '@/components/modules/place/PlaceDetails';
import PlaceInfo from '@/components/modules/place/PlaceInfo';
import Seo from '@/components/modules/place/Seo';
import ServiceList from '@/components/modules/place/ServiceList';
import { InstagramFeed } from '@/components/modules/salon';
import Bundles from '@/components/modules/salon/Bundles';
import Certificates from '@/components/modules/salon/Certificates';
import EmptyState from '@/components/templates/EmptyState';
import { SCREEN_NAME } from '@/constants/analytics';
import { NEW_PLACE_TWEAKS_EXPERIMENT, REVIEWS_V2_LISTCARDS_V2_FLAG } from '@/constants/experimentConstants';
import { ROUTE_BUILDER } from '@/constants/pages';
import { PlacePageReviews } from '@/features/PlacePages/modules/PlacePageReviews';

// NOTE: The following imports are for legacy components related to employees and reviews.
import LegacyEmployeeListCardV1 from '@/components/elements/Card/LegacyEmployeeListCardV1';
import LegacyEmployeeListCardV2 from '@/components/elements/Card/LegacyEmployeeListCardV2';
import LegacyEmployeeListCardV3 from '@/components/elements/Card/LegacyEmployeeListCardV3';
import { LegacyEmployeeReviews } from '@/components/modules/employee';
import LegacyReviews, { LegacyReviewsHeader } from '@/features/PlacePages/modules/LegacyReviews';

import { ReviewsModal } from '@/features/reviews/ReviewsModal';
import { useListCardsV2 } from '@/hooks/useListCardsV2';
import { useSelector } from 'react-redux';
import { modalActions } from '@/actions/modalActions';

import {
  capitalizeFirstLetter,
  classnames,
  getBookingStartingPoint,
  getDetailsBreadcrumbs,
  hasFeatureSetting,
  isBookable,
  isServiceBookable,
  shouldDisplayReviews,
} from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import {
  formatPhoneNumber,
  getGiftcardCheckoutLink,
  getImages,
  getMerchantKlarnaClientId,
  getOpenHoursStrings,
} from '@/helpers/placeHelperTS';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { useConfidenceFlag, useConfidenceFlagContext } from '@/hooks/useConfidenceFlag';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { getLocale } from '@/locale/i18n';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { Place, PlaceEmployee } from '@/types/state/shared';
import type { ButtonID } from '@bokadirekt/tracking-schema/marketplace';
import { toast } from 'react-toastify';
import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import FakeSearchForm from '../searchV2/modules/FakeSearchForm/FakeSearchForm';
import TabBar from './components/TabBar';
import { getCompanyWebsiteWithProcotol, getMatchedServicesTitle, mapOtherPlaces } from './helpers/placeData';
import { trackPlaceButtonClicked } from './helpers/tracking';
import Categories from './modules/Categories';
import Giftcards from './modules/Giftcards';
import PlacePageHeader from './modules/PlacePageHeader';
import ListView from './modules/ListView';
import LoggedOutFavouriteModal from './modules/modals/LoggedOutFavouriteModal';
import ServiceReadMoreModal from './modules/modals/ServiceReadMoreModal';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';
import { usePlacePage } from './PlacePage.hooks';
import { NormalizedPlace } from '@/types/place';
import { ReviewsState } from '@/reducers/reviewsReducer';
import { TIME_FILTER } from '@/constants/timeConstants';

export const baseTranslationKey = 'pages.places.$slug';

type PlacePageProps = {
  place: Place;
  placeId: number;
  isSistaminuten?: boolean;
};

const PlacePage = ({ place, isSistaminuten, placeId }: PlacePageProps) => {
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const location = useLocation<{ fromList?: boolean; searchParams?: Record<string, string> }>();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.users?.user);
  const isWhitelabel = useAppSelector((state) => state?.settings.isWhitelabel);
  const getVariant = useGetAmplitudeExperimentVariant();

  const useMegaMenuFeature = useConfidenceFlagContext().flagValue.enabled && !isWhitelabel;

  const tooltipTargetRef = useRef(null);
  const headerTargetRef = useRef(null);
  const searchInputRef = useRef(null);

  const realHeaderPosition = useIntersectionObserver(headerTargetRef, {
    root: null,
    threshold: useMegaMenuFeature ? 0.1 : 1,
    rootMargin: '-64px 0px 0px 0px',
  });

  const headerPosition = headerTargetRef?.current && realHeaderPosition === null ? 'inView' : realHeaderPosition;

  const [readMore, setReadMore] = useState<{ title: string; text: string; id: number }>(undefined);
  const [showFullPhoneNumber, setShowFullPhoneNumber] = useState(false);
  const [serviceBooking, setServiceBooking] = useState<{ serviceId: number; book: () => void }>();
  const isReviewsModalShown = useAppSelector((state) =>
    Boolean((state.modal as { reviews?: ReviewsModalType } | undefined)?.reviews?.show),
  );
  const [showLoggedOutFavouriteModal, setShowLoggedOutFavouriteModal] = useState(false);
  const { show: showNavigationDrawer = false } = useAppSelector((state) => state?.navigationDrawer) as {
    show: boolean;
  };
  const {
    matchedServices,
    onRemoveSearch,
    search,
    showSearch,
    searchedBundles,
    searchedServices,
    onShowSearchInput,
    onSearch,
    onEmployeeClick,
    onCloseLegacyEmployeeReviews,
    onPickEmployee,
    isSticky,
    legacyEmployeeReviews,
    activeTab,
    tabRefs,
  } = usePlacePage(place, headerTargetRef, useContext(BookAppointmentContext), isSistaminuten, isWhitelabel);

  const isMpReviews = !['1', 'true'].includes(searchParams.get('service-reviews'));
  const { about = {}, contact = {} } = place;

  const reviewsUid = `${placeId}_page`;

  const {
    showBundlesOnTop = true,
    hideEmployees,
    displayCertificates,
    openingHoursExtraInformation,
  } = about.settings || {};

  const hideStaff = Boolean(place.schedule) || hideEmployees;
  const placeImages = getImages(place);
  const breadcrumbs = getDetailsBreadcrumbs(place);
  const placeEmployeesCustomTitle =
    place && place.about && place.about.employeesAlias
      ? place.about.employeesAlias
      : _t(`${baseTranslationKey}.employeesTitle`);

  const serviceCategory = about.category?.main ?? about.category?.sub;
  const companyPhoneNumber = contact.phone ? formatPhoneNumber(contact.phone) : undefined;
  const companyEmail = contact.email;

  const normalizedPlace = useSelector((state: { place: Record<number, NormalizedPlace> }) => state.place[placeId]);
  const placeReviews = useSelector((state: { reviews: ReviewsState }) => state.reviews?.byUid?.[reviewsUid]?.details);

  const { flagValue: reviewsV2FlagValue, isReady: isReviewsV2FlagReady } = useConfidenceFlag(
    REVIEWS_V2_LISTCARDS_V2_FLAG.name,
    REVIEWS_V2_LISTCARDS_V2_FLAG.defaultValue,
    true,
  );
  const isReviewsV2Enabled = Boolean(reviewsV2FlagValue?.['reviews-v2']);
  const isListCardsV2 = useListCardsV2();
  const haveReviews = Boolean(placeReviews?.stats?.count > 4);

  const initialReviewsSearchFilter = useMemo(
    () => ({
      placeId: Number(place.id),
      page: 1,
      limit: REVIEWS_PAGE_LIMIT,
      isMpReviews,
      hasMessage: true,
      timeFilter: TIME_FILTER.FOREVER as SelectedTimeFilterOptions,
    }),
    [place.id, isMpReviews],
  );

  useReviews({
    uid: reviewsUid,
    searchFilter: initialReviewsSearchFilter,
    initiateState: true,
    originType: 'placePage',
    enabled: isReviewsV2FlagReady,
  });

  const hasCollapsedSetting = hasFeatureSetting(place, 'collapsed_services');
  const showListView = place.schedule && place.services && place.services.length;
  const servicesCategories = place.services;

  // TODO. Missing services should look at the normalized Place
  const missingServices = useMemo(() => {
    return !servicesCategories?.some(
      (serviceCategory) =>
        (serviceCategory.services.length > 0 &&
          serviceCategory.services.some((service) => isServiceBookable(service, place))) ||
        place.isExternalPlace,
    );
  }, [servicesCategories, place]);

  const giftcardLink = getGiftcardCheckoutLink(place);

  const bundlesSectionPlacement: 'top' | 'bottom' | undefined = (() => {
    if (isSistaminuten || Boolean(search)) return undefined;

    if (showBundlesOnTop) {
      return 'top';
    }

    return 'bottom';
  })();

  const giftcardSectionPlacement: 'top' | 'bottom' | undefined = (() => {
    if (!giftcardLink || isSistaminuten) return undefined;

    if (matchedServices.length) return 'bottom';

    return 'top';
  })();

  const companySocials = {
    instagram: {
      name: place.contact?.instagram,
      link: place.contact?.instagram ? `https://www.instagram.com/${place.contact.instagram}` : undefined,
    },
    facebook: {
      name: place.contact?.facebook,
      link: place.contact?.facebook ? `https://www.facebook.com/${place.contact.facebook}` : undefined,
    },
    website: getCompanyWebsiteWithProcotol(place),
  };
  const haveCompanySocials = Boolean(
    companySocials.instagram.name || companySocials.facebook.name || companySocials.website,
  );
  const openingHours = getOpenHoursStrings(place.program);
  const showInstagramFeed = contact?.instagram && Boolean(about.instagramFeed.length);

  useEffect(() => {
    if (showSearch && searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    getVariant(NEW_PLACE_TWEAKS_EXPERIMENT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => {
    if (location.state?.fromList) {
      history.goBack();
    } else {
      if (isSistaminuten) {
        history.replace(ROUTE_BUILDER.sistaMinutenSerp());
      } else {
        history.replace(ROUTE_BUILDER.serp(DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION));
      }
    }
  };

  const handleSearchInputBlur = () => {
    if (searchInputRef?.current && !search) {
      onRemoveSearch();
    }
  };

  const handleShowFullPhoneNumber = () => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_SHOW_PHONE_NUMBER_CLICK);
    setShowFullPhoneNumber(true);
  };

  const handlePhoneNumberClick = async () => {
    await placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_PHONE_NUMBER_CLICK);
    window.location.href = `tel:${companyPhoneNumber.fullNumber}`;
  };

  const handleClickReadMore = (title: string, text: string, id: number) => {
    setReadMore({ title, text, id });
  };

  const openReviewsModal = (serviceId: number, book: () => void) => {
    setServiceBooking({ serviceId, book });
    dispatch(
      modalActions.reviews({
        show: true,
        placeId: place.id,
        focus: 'service',
        context: 'placePage',
        serviceId: serviceId,
      }),
    );
  };

  // The row's booking entry belongs to that one open; a later open from elsewhere must not inherit it.
  useEffect(() => {
    if (!isReviewsModalShown) setServiceBooking(undefined);
  }, [isReviewsModalShown]);

  // The footer books the service the row opened through the row's own booking entry; one reached by
  // navigating inside the modal keeps the modal's default redirect. So does a picked employee: the
  // row's entry always starts without one, and only the redirect carries it. Without list cards v2
  // (reviews v2 alone) the footer keeps master's redirect to the calendar.
  const getServiceCta = (serviceId: number, employeeId?: number) =>
    isListCardsV2 && serviceBooking && serviceId === serviceBooking.serviceId && !employeeId
      ? { label: _t('BookService'), onClick: serviceBooking.book }
      : undefined;

  const handleCloseReadMore = () => {
    setReadMore(undefined);
  };

  const handleAddToFavorites = () => {
    if (!user) {
      setShowLoggedOutFavouriteModal(true);
      return;
    }

    const isPlaceAlreadyFavorite = user.favorites?.indexOf(place.id) !== -1;

    const tracking = { companyName: about.name, initiatedFrom: SCREEN_NAME.COMPANY_DETAILS };
    const dispatchAction = isPlaceAlreadyFavorite
      ? favoriteActions.remove(place.id, user, tracking)
      : favoriteActions.add(place.id, user, tracking);

    // @ts-ignore
    dispatch(dispatchAction).then(() => {
      toast(({ closeToast }) => (
        <Snackbar
          type="success"
          onClose={closeToast}
          label={
            !isPlaceAlreadyFavorite
              ? _t(`${baseTranslationKey}.favorites.added`)
              : _t(`${baseTranslationKey}.favorites.removed`)
          }
        />
      ));
    });
  };

  const handleButtonClick = (buttonId: ButtonID) => trackPlaceButtonClicked(place, buttonId);

  const handleExternalWebsiteClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_EXTERNAL_BUSINESS_LINK_CLICK);
  };

  const handleOnPickEmployee = (employee: PlaceEmployee) => {
    onCloseLegacyEmployeeReviews();
    onPickEmployee(employee);
    bookingFlowTrackingSession.initiate(place, user, getBookingStartingPoint(), {
      employee_selection: 'specific',
    });
  };

  // Legacy-view only: employeeStats comes from the same redux-backed placeReviews used
  // everywhere else in the hybrid view, not from place.reviews.employeeStats like on master.
  const getEmployeeCardProps = (employee: PlaceEmployee) => {
    const employeeStats = placeReviews?.employeeStats;
    const employeeRating = employeeStats?.[employee.id]?.score ? employeeStats[employee.id] : undefined;
    const showBookButton = isBookable({ employee, place });

    return {
      employee: employee,
      stats: employeeRating,
      onClick: showBookButton || employeeRating ? () => onEmployeeClick(employee, employeeRating) : undefined,
      rightSlot: showBookButton ? (
        <Button
          label={_t('chooseService')}
          data-cy="chooseEmployeeButton"
          onClick={() => handleOnPickEmployee(employee)}
          size="sm"
          variant={'primary'}
        />
      ) : undefined,
    };
  };

  const showNonStickyTabBar = () => {
    if (headerPosition === 'inView' || headerPosition === 'belowViewport') {
      return false;
    }

    if (isMobileView && !isSticky) {
      return false;
    }

    if (!isMobileView && !headerTargetRef.current) {
      return false;
    }

    return true;
  };

  const mainImages = place?.about?.visibleMainImages || (placeImages?.length > 5 ? 5 : placeImages?.length);

  const getEmployeeList = () => {
    if (isReviewsV2Enabled && normalizedPlace?.employees?.length) {
      const EmployeeCard = isListCardsV2 ? EmployeeListCard : LegacyEmployeeListCardV2;
      return normalizedPlace.employees.map((employeeId, index) => {
        return (
          <EmployeeCard
            key={`${place.id}_${employeeId}-${index}`}
            reviewsUid={reviewsUid}
            placeId={place.id}
            employeeId={employeeId}
            handleOnPickEmployee={handleOnPickEmployee}
          />
        );
      });
    }

    if (place?.employees?.length) {
      const EmployeeCard = isListCardsV2 ? LegacyEmployeeListCardV3 : LegacyEmployeeListCardV1;
      return place.employees
        .filter((employee) => !employee.about?.inactive)
        .map((employee, index) => <EmployeeCard key={index} {...getEmployeeCardProps(employee)} />);
    }

    return null;
  };

  return (
    <>
      <PageViewLayout
        back={!isWhitelabel}
        hideBottomNavBar
        title={about.name}
        type="mainView"
        onBackButtonClick={goBack}
        megaMenuPlaceFlag={useMegaMenuFeature}
        wrapperClass="bg-surface_mp"
        /**
         * Hide bokadirekt logo on mobile view
         */
        leftSlot={
          isMobileView && !isWhitelabel ? (
            <Fragment>
              {isSticky && useMegaMenuFeature && (
                <div className="gap-xxs flex flex-col justify-center">
                  <div className="text-l">{about.name}</div>
                  <RatingV2
                    ratingCount={
                      placeReviews?.stats?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? placeReviews?.stats?.count : 0
                    }
                    averageRating={placeReviews?.stats?.score}
                    size={'sm'}
                    withAverageScore={placeReviews?.stats?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED}
                    withRatingCounter
                    withMultipleStars={false}
                  />
                </div>
              )}
            </Fragment>
          ) : !isWhitelabel ? (
            <Link to="/" aria-label="Bokadirekt" className={'hidden lg:block'}>
              <Logotype className="not-sr-only" variant="logo" style={{ height: 22 }} />
            </Link>
          ) : (
            <span aria-label="Bokadirekt">
              <Logotype className="not-sr-only" variant="logo" style={{ height: 22 }} />
            </span>
          )
        }
        middleSlot={(() => {
          if (!useMegaMenuFeature) {
            return null;
          }

          const type = isSistaminuten ? 'sistaminuten' : 'default';

          if (!isMobileView) {
            return <FakeSearchForm type={type} />;
          }

          if (!isSticky) {
            return <FakeSearchForm type={type} />;
          }
          return null;
        })()}
        /**
         * Move tab bar to bottomslot when scrolled down
         */
        {...((showNonStickyTabBar() || (isSticky && isMobileView)) &&
          useMegaMenuFeature && {
            bottomSlot: (
              <TabBar
                top
                withReviews={shouldDisplayReviews(place)}
                withEmployees={!hideStaff}
                placeId={place.id}
                onFavoriteClick={handleAddToFavorites}
                activeTab={activeTab}
                className={classnames(
                  isMobileView && 'h-[64px]',
                  !isMobileView && 'px-md h-[72px]',
                  'flex justify-center',
                )}
                placeName={about.name}
                reviews={{ count: placeReviews?.stats?.count ?? 0, rating: placeReviews?.stats?.score }}
              />
            ),
          })}>
        <div className="text-fg_primary flex justify-center">
          <div
            className={classnames(
              `px-md pb-3xl gap-cv-margin flex w-full max-w-[1040px] flex-col`,
              !isWhitelabel ? 'pt-md' : 'pt-3xl',
            )}>
            {!isWhitelabel && <Breadcrumbs items={breadcrumbs} className="w-full text-sm" />}

            <div className={`flex flex-col ${isMobileView ? 'gap-xl' : 'gap-md'}`}>
              <PlacePageHeader placeId={placeId} onFavoriteClick={handleAddToFavorites} reviewsUid={reviewsUid} />
              {about.temporaryMessage && (
                <Alert
                  leftSlot={<Icon variant="info-circle" color="fg_info_bold" />}
                  body={about.temporaryMessage}
                  variant="information"
                />
              )}
            </div>
            {getMerchantKlarnaClientId(place) && (
              // @ts-ignore
              <klarna-placement
                id="klarna-placement"
                data-key="top-strip-promotion-auto-size"
                data-locale={getLocale()}
              />
            )}

            <div
              className={classnames(
                `border-border_regular fixed left-0 top-[64px] z-[99] w-screen border-t transition-opacity duration-200`,
                showNonStickyTabBar() ? 'visible opacity-100' : 'invisible opacity-0',
              )}>
              <TabBar
                className={classnames(
                  isMobileView && 'px-lg h-[64px]',
                  !isMobileView && 'px-md h-[72px]',
                  'flex justify-center',
                  'drop-shadow-xs',
                )}
                withReviews={shouldDisplayReviews(place)}
                withEmployees={!hideStaff}
                placeId={place.id}
                onFavoriteClick={handleAddToFavorites}
                activeTab={activeTab}
                placeName={about.name}
                reviews={{ count: placeReviews?.stats?.count ?? 0, rating: placeReviews?.stats?.score }}
                top
              />
            </div>

            <div
              ref={headerTargetRef}
              style={{ top: 64, ...(isMobileView && { height: 64 }) }}
              className={classnames(
                isMobileView && 'sticky',
                isSticky && isMobileView ? '-ml-[16px] w-[calc(100%+32px)]' : '',
                !useMegaMenuFeature && 'z-[98]',
              )}>
              <TabBar
                top={isSticky && isMobileView}
                className={classnames(
                  isMobileView && 'px-lg h-[64px]',
                  !isMobileView && 'px-md h-[72px]',
                  isSticky && isMobileView ? 'flex justify-center' : 'border-border_regular rounded-lg border-b',
                  'drop-shadow-xs',
                )}
                placeId={place.id}
                withEmployees={!hideStaff}
                withReviews={shouldDisplayReviews(place)}
                onFavoriteClick={handleAddToFavorites}
                activeTab={activeTab}
                reviews={{ count: placeReviews?.stats?.count ?? 0, rating: placeReviews?.stats?.score }}
              />
            </div>

            {[1, 3].indexOf(mainImages) === -1 && <Photos photos={placeImages} place={place} />}

            <div className={`flex ${isMobileView ? 'gap-2xl flex-col' : 'gap-xl justify-between'}`}>
              <div className="gap-cv-margin flex min-w-0 flex-1 flex-col">
                {[1, 3].indexOf(mainImages) !== -1 && placeImages?.length > 0 && (
                  <Photos photos={placeImages} place={place} />
                )}

                {matchedServices?.length > 0 && (
                  <div className="flex flex-col">
                    <ListItem
                      type="div"
                      leftPadding={false}
                      leftSlot={<Icon variant="search-check" size="md" />}
                      rightSlot={
                        <span>
                          {getMatchedServicesTitle({
                            ...location.state?.searchParams,
                          } as { q: string; startDate: string; endDate: string; timeOfDay: string; prefs: string })}
                        </span>
                      }>
                      <h2 className="text-h-m">{_t(`${baseTranslationKey}.matchedServices.title`)}</h2>
                    </ListItem>

                    <div ref={tabRefs.servicesTabRef} id="services" className="scroll-mt-[144px]">
                      <ServiceList
                        place={place}
                        services={matchedServices}
                        _expanded={true}
                        handleClickReadMore={handleClickReadMore}
                        onServiceClick={openReviewsModal}
                      />
                    </div>
                  </div>
                )}

                {giftcardSectionPlacement === 'top' && <Giftcards placeId={place.id} />}

                <div className="flex flex-col">
                  {!missingServices && !showListView && (
                    <ListItem
                      type="div"
                      leftPadding={false}
                      rightPadding={false}
                      rightSlot={
                        !showSearch && (
                          <Fab
                            size="sm"
                            variant="secondary"
                            aria-label={_t(`${baseTranslationKey}.search.label`)}
                            onClick={onShowSearchInput}
                            rightIcon={<Icon variant="search" color="fg_primary" />}
                          />
                        )
                      }>
                      <div className="flex items-center justify-between">
                        {(!showSearch || (showSearch && !isMobileView)) && (
                          <h2 className="text-h-m font-semibold">{_t(`${baseTranslationKey}.servicesTitle`)}</h2>
                        )}
                        {showSearch && (
                          <div className={isMobileView ? 'flex-1' : ''}>
                            <SearchInput
                              value={search}
                              onChange={onSearch}
                              onRemove={onRemoveSearch}
                              onBlur={handleSearchInputBlur}
                              ref={searchInputRef}
                              placeholder={_t(`${baseTranslationKey}.search.placeholder`)}></SearchInput>
                          </div>
                        )}
                      </div>
                    </ListItem>
                  )}

                  {!missingServices && showListView && (
                    <ListItem type="div" leftPadding={false} leftSlot={<Icon variant="tags" size="md" />}>
                      <h2 className="text-h-m">{_t(`${baseTranslationKey}.servicesTitle`)}</h2>
                    </ListItem>
                  )}

                  <div id="services" className="scroll-mt-[144px]">
                    {missingServices && (
                      <Card size={isMobileView ? 'lg' : 'xl'}>
                        <div className="gap-2xl flex flex-col">
                          <div className="flex items-center justify-between">
                            <h2 className="text-h-s">{_t(`${baseTranslationKey}.servicesCard.title`)}</h2>
                          </div>
                          <EmptyState
                            title={_t(`${baseTranslationKey}.servicesCard.emptyState.title`)}
                            icon={<img src="/images/illustrations/empty.svg" alt="" />}
                          />
                        </div>
                      </Card>
                    )}

                    {bundlesSectionPlacement === 'top' && <Bundles isOpen={!hasCollapsedSetting} place={place} />}

                    {!isSistaminuten && !showListView && Boolean(searchedBundles.length) && (
                      <Bundles
                        isOpen={!hasCollapsedSetting}
                        title={_t(`${baseTranslationKey}.search.matchedBundles`)}
                        place={{ ...place, bookingBundles: searchedBundles }}
                      />
                    )}

                    {!showListView && Boolean(searchedServices.length) && (
                      <ServiceList
                        place={place}
                        categoryName={_t(`${baseTranslationKey}.search.matchedServices`)}
                        services={searchedServices}
                        handleClickReadMore={handleClickReadMore}
                        onServiceClick={openReviewsModal}
                        _expanded={true}
                      />
                    )}

                    {!showListView && (Boolean(searchedServices.length) || Boolean(searchedBundles.length)) && (
                      <Button
                        label={_t(`${baseTranslationKey}.search.showAllCta`)}
                        variant="link"
                        onClick={onRemoveSearch}
                        size="md"
                      />
                    )}

                    {Boolean(search) && !Boolean(searchedBundles.length) && !Boolean(searchedServices.length) && (
                      <Card size={isMobileView ? 'lg' : 'xl'}>
                        <div className="gap-2xl flex flex-col">
                          <EmptyState
                            body={_t(`${baseTranslationKey}.search.emptyState.body`, {
                              search: search,
                            })}
                            title={_t(`${baseTranslationKey}.search.emptyState.title`)}
                            icon={<img src="/images/illustrations/empty.svg" alt="" />}
                            cta={
                              <Button
                                label={_t(`${baseTranslationKey}.search.showAllCta`)}
                                variant="link"
                                onClick={onRemoveSearch}
                                size="md"
                              />
                            }
                          />
                        </div>
                      </Card>
                    )}

                    {!missingServices && !showListView && !Boolean(search) && (
                      <Categories
                        onClickReadMore={handleClickReadMore}
                        onServiceClick={openReviewsModal}
                        place={place}
                      />
                    )}
                    {showListView && <ListView place={place} />}
                    {bundlesSectionPlacement === 'bottom' && <Bundles isOpen={!hasCollapsedSetting} place={place} />}
                  </div>
                </div>
                {giftcardSectionPlacement === 'bottom' && <Giftcards placeId={place.id} />}
                {!hideStaff && (
                  <div
                    id={'employees'}
                    className="gap-md flex scroll-mt-[144px] flex-col"
                    ref={tabRefs.employeesTabRef}>
                    <ListItem type="div" leftPadding={false}>
                      <h2 className="text-h-m text-fg-primary font-semibold">
                        {capitalizeFirstLetter(placeEmployeesCustomTitle)}
                      </h2>
                    </ListItem>
                    {getEmployeeList()}
                  </div>
                )}
                {isMobileView && shouldDisplayReviews(place) && (
                  <>
                    <div className="relative" ref={tooltipTargetRef}></div>
                    <div id="reviews" className="scroll-mt-[144px]" ref={tabRefs.reviewsTabRef}>
                      {isReviewsV2Enabled ? (
                        <>
                          <PlacePageReviews placeId={place.id} isMpReviews={isMpReviews} reviewsUid={reviewsUid} />
                        </>
                      ) : (
                        <>
                          {haveReviews && (
                            <LegacyReviews
                              place={place}
                              reviewsUid={reviewsUid}
                              hideStaff={false}
                              isMpReviews={isMpReviews}
                              tooltipTargetRef={tooltipTargetRef}
                            />
                          )}
                          {!haveReviews && (
                            <Card size={'lg'}>
                              <div className="gap-2xl flex flex-col">
                                <LegacyReviewsHeader />
                                <EmptyState
                                  title={_t('notEnoughReviews')}
                                  icon={<img src="/images/illustrations/empty.svg" alt="" />}
                                />
                              </div>
                            </Card>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}

                {isMobileView && (
                  <>
                    <PlaceDetails place={place} isVerified={true} isBasic={false} isPremium={true} />
                    {(companyEmail || companyPhoneNumber) && (
                      <Contact
                        placeId={place.id}
                        email={companyEmail}
                        isVerified={true}
                        onShowFullPhoneNumber={handleShowFullPhoneNumber}
                        onPhoneNumberClick={handlePhoneNumberClick}
                        onButtonClick={handleButtonClick}
                        phone={
                          companyPhoneNumber
                            ? showFullPhoneNumber
                              ? companyPhoneNumber.fullNumber
                              : companyPhoneNumber.hiddenNumber
                            : undefined
                        }
                        fullNumber={showFullPhoneNumber}
                      />
                    )}

                    {haveCompanySocials && (
                      <Links
                        placeId={place.id}
                        onWebsiteClick={handleExternalWebsiteClick}
                        onButtonClick={handleButtonClick}
                        {...companySocials}
                      />
                    )}

                    <Address place={place} />
                    {(openingHours || openingHoursExtraInformation) && (
                      <OpeningHours openingHours={openingHours} additionalInfo={openingHoursExtraInformation} />
                    )}
                  </>
                )}

                {!isMobileView && shouldDisplayReviews(place) && (
                  <div id={'reviews'} className={'scroll-mt-[164px]'} ref={tabRefs.reviewsTabRef}>
                    <div className="relative" ref={tooltipTargetRef}></div>
                    {isReviewsV2Enabled ? (
                      <PlacePageReviews placeId={place.id} reviewsUid={reviewsUid} isMpReviews={isMpReviews} />
                    ) : (
                      <>
                        {haveReviews && (
                          <LegacyReviews
                            place={place}
                            reviewsUid={reviewsUid}
                            hideStaff={false}
                            isMpReviews={isMpReviews}
                            tooltipTargetRef={tooltipTargetRef}
                          />
                        )}
                        {!haveReviews && (
                          <Card size={'xl'}>
                            <div className="gap-2xl flex flex-col">
                              <LegacyReviewsHeader />
                              <EmptyState
                                title={_t('notEnoughReviews')}
                                icon={<img src="/images/illustrations/empty.svg" alt="" />}
                              />
                            </div>
                          </Card>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div id="about" ref={tabRefs.aboutTabRef} className="scroll-mt-[144px]">
                  <PlaceInfo place={place} category={serviceCategory} isVerified={true} year={undefined} />
                </div>

                {displayCertificates && (
                  <div id="certificates" className="scroll-mt-[144px]">
                    <Card size={'xl'}>
                      <Certificates employees={place.employees} />
                    </Card>
                  </div>
                )}

                {showInstagramFeed && (
                  <div className="py-6">
                    <InstagramFeed profile={contact.instagram} media={about.instagramFeed} withCard withInstagramIcon />
                  </div>
                )}
                {place.locations?.length > 0 && about.salonId && (
                  <Companies
                    companies={mapOtherPlaces(place.locations, place.id)}
                    bottomSlot={
                      <div className="pt-md flex justify-start">
                        <LinkButton
                          className={'!pl-0'}
                          to={`/goto/${about.salonId}`}
                          label={_t(`${baseTranslationKey}.showAllLocations`)}
                          size="md"
                        />
                      </div>
                    }
                    title={{
                      bold: _t(`otherLocations`),
                    }}
                  />
                )}
              </div>

              <div className={`flex flex-col ${isMobileView ? 'gap-2xl' : 'gap-md max-w-[336px]'}`}>
                {!isMobileView && (
                  <>
                    <PlaceDetails place={place} isVerified={true} isBasic={false} isPremium={true} />
                    {(openingHours || openingHoursExtraInformation) && (
                      <OpeningHours openingHours={openingHours} additionalInfo={openingHoursExtraInformation} />
                    )}
                    {(companyEmail || companyPhoneNumber) && (
                      <Contact
                        placeId={place.id}
                        email={companyEmail}
                        isVerified={true}
                        onShowFullPhoneNumber={handleShowFullPhoneNumber}
                        onPhoneNumberClick={handlePhoneNumberClick}
                        onButtonClick={handleButtonClick}
                        phone={
                          companyPhoneNumber
                            ? showFullPhoneNumber
                              ? companyPhoneNumber.fullNumber
                              : companyPhoneNumber.hiddenNumber
                            : undefined
                        }
                        fullNumber={showFullPhoneNumber}
                      />
                    )}
                    {haveCompanySocials && (
                      <Links
                        placeId={place.id}
                        onWebsiteClick={handleExternalWebsiteClick}
                        onButtonClick={handleButtonClick}
                        {...companySocials}
                      />
                    )}
                    <Address place={place} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <BookAppointment />

        <LoggedOutFavouriteModal
          isOpen={showLoggedOutFavouriteModal}
          onClose={() => setShowLoggedOutFavouriteModal(false)}
        />
        {(isReviewsV2Enabled || isListCardsV2) && (
          <ReviewsModal
            placeId={place.id}
            hideStaff={hideStaff}
            tooltipTargetRef={tooltipTargetRef}
            getServiceCta={getServiceCta}
          />
        )}
        {!isReviewsV2Enabled && legacyEmployeeReviews && (
          <LegacyEmployeeReviews
            isOpen={true}
            close={onCloseLegacyEmployeeReviews}
            employee={legacyEmployeeReviews.employee}
            place={place}
            rating={legacyEmployeeReviews.rating}
            isMpReviews={isMpReviews}
            pickService={() =>
              isBookable({ place, employee: legacyEmployeeReviews.employee })
                ? handleOnPickEmployee(legacyEmployeeReviews.employee)
                : null
            }
          />
        )}

        {isMobileView && !showNavigationDrawer && <NavigationDrawer />}
        {isMobileView && showNavigationDrawer && <BottomNavBar megaMenuPlaceFlag={useMegaMenuFeature} />}

        <ServiceReadMoreModal
          isOpen={Boolean(readMore?.text)}
          onClose={handleCloseReadMore}
          place={place}
          id={readMore?.id}
          text={readMore?.text}
          title={readMore?.title}
        />
      </PageViewLayout>
      {Seo({ place: place, isSistaminuten: isSistaminuten })}
    </>
  );
};

export default PlacePage;
